import React, { useEffect, useMemo } from "react";
import Container from "react-bootstrap/Container";
import Table from "react-bootstrap/Table";
import Layout from "../Components/Layout/Layout";
import { useState } from "react";
import CreateClientModal from "../Components/CommanModals/CreateClientModal";
import { useDispatch, useSelector } from "react-redux";
import { createClient, getClients, updateClient } from "../Redux/Actions/auth";
import { toast } from "react-toastify";
import { startstopLoading } from "../Redux/Reducers/globalSlice";
import NoData from "../Components/NoData";
import {
  capitalize,
  dateOfBookingEventWise,
  downloadCSV,
  formattedDate,
} from "../Utils/commonFiles";
import PaginationComponent from "../Components/Pagination";
import { useLocation, useNavigate } from "react-router-dom";
import { Encryptedid } from "../Utils/bcrypt";
import NotesViewModal from "../Components/CommanModals/NotesViewModal";
import { downClientsCSV } from "../Redux/Actions/event";
import DatePickerComponent from "../Components/DatePickerComponent";

const AVATAR_COLORS = [
  "#A8C4F5", "#F5C4A0", "#A8DFA6", "#D0A8E8", "#F5A8A8",
  "#A8E8D8", "#F5DCA0", "#A8C8F0", "#F5A8D8", "#A8D8B8",
  "#F5B8A8", "#A8DDE8", "#D8A8E8", "#A8D8D4", "#F5B8B8",
];

const getAvatarColor = (name = "") => {
  const code = (name || "").trim().toUpperCase().charCodeAt(0) || 0;
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
};

export default function Clients() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [showModalNotes, setShowModalNotes] = useState(false);
  const [selectedNotes, setSelectedNotes] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);

  const getClientsList = useSelector((e) => e.userAuth.clientList);

  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });

  // const handleSort = (key) => {
  //   setSortConfig((prevConfig) => ({
  //     key,
  //     direction:
  //       prevConfig.key === key && prevConfig.direction === "asc"
  //         ? "desc"
  //         : "asc",
  //   }));
  // };
  // derive clients array safely

  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      const newDirection =
        prevConfig.key === key && prevConfig.direction === "asc"
          ? "desc"
          : "asc";

      // update URL so sorting persists on navigation
      const params = new URLSearchParams(location.search);
      params.set("sort", key);
      params.set("direction", newDirection);
      navigate(`/clients?${params.toString()}`);

      return { key, direction: newDirection };
    });
  };

  const clients = getClientsList?.client || [];

  // memoized sorted clients
  const sortedClients = useMemo(() => {
    const arr = [...clients];
    const { key, direction } = sortConfig;

    arr.sort((a, b) => {
      let valA = null;
      let valB = null;

      if (key === "createdAt") {
        valA = a?.createdAt ? new Date(a.createdAt) : null;
        valB = b?.createdAt ? new Date(b.createdAt) : null;
      } else if (key === "eventDate") {
        // eventDate might be an array or a single value — pick first if array
        const aDate = Array.isArray(a?.eventDate)
          ? a.eventDate[0]
          : a?.eventDate;
        const bDate = Array.isArray(b?.eventDate)
          ? b.eventDate[0]
          : b?.eventDate;
        valA = aDate ? new Date(aDate) : null;
        valB = bDate ? new Date(bDate) : null;
      } else {
        return 0;
      }

      // put null/undefined dates at the end
      if (!valA && !valB) return 0;
      if (!valA) return direction === "asc" ? 1 : -1;
      if (!valB) return direction === "asc" ? -1 : 1;

      return direction === "asc" ? valA - valB : valB - valA;
    });

    return arr;
  }, [clients, sortConfig]);

  const handleClose = () => {
    setShowModal(false);
    setSelectedClient(null);
  };

  const handleShow = () => {
    setShowModal(true);
  };

  const handleCreateClient = (clientData) => {
    dispatch(startstopLoading(true));

    const isUpdate = !!selectedClient?._id;
    const action = isUpdate ? updateClient : createClient;

    dispatch(action(clientData))
      .then((res) => {
        dispatch(startstopLoading(false));

        if (res?.payload?.statusCode === 200) {
          const message = isUpdate
            ? "Client Updated Successfully"
            : "Client Created Successfully";
          toast.success(message);
          dispatch(getClients({ page: currentPage, limit: 10 }));
          handleClose();
        } else {
          toast.error(res?.payload?.message || "Failed to process the request");
        }
      })
      .catch((error) => {
        console.error("Error in handleCreateClient:", error);
        toast.error("An error occurred. Please try again.");
      });
  };

  const handleViewModalShow = (client) => {
    setSelectedClient(client);
    handleShow();
  };

  const totalEntries = getClientsList?.total ?? 0;
  const entriesPerPage = 10;

  const totalPages = Math.ceil(totalEntries / entriesPerPage);

  // const handlePageChange = (page) => {
  //   setCurrentPage(page);
  //   navigate(`/clients?page=${page}`);
  //   // dispatch(getClients({ page: page, limit: 10 }))
  // };

  const handlePageChange = (page) => {
    const params = new URLSearchParams(location.search);
    params.set("page", page);

    // preserve sorting params
    if (sortConfig.key) {
      params.set("sort", sortConfig.key);
      params.set("direction", sortConfig.direction);
    }

    navigate(`/clients?${params.toString()}`);
  };

  const handleClientDetail = (data) => {
    navigate(`/client-detail/${Encryptedid(data?._id)}`);
  };

  const handleViewNotes = (notes) => {
    setSelectedNotes(notes);
    setShowModalNotes(true);
  };

  const handleCheckboxChange = (userId, isChecked) => {
    if (isChecked) {
      // Add user ID to selectedUsers array
      setSelectedUsers((prevState) => [...prevState, userId]);
    } else {
      // Remove user ID from selectedUsers array
      setSelectedUsers((prevState) => prevState.filter((id) => id !== userId));
    }
  };

  const handleExportButtonClick = async () => {
    let data = {
      ids: selectedUsers,
    };
    try {
      await dispatch(downClientsCSV(data)).then((res) => {
        debugger;
        return;
        downloadCSV("");
      });
    } catch (error) { }
  };

  // useEffect(() => {
  //   if (location.pathname === "/clients") {
  //     const params = new URLSearchParams(location.search);
  //     const page = params.get("page") || 0;
  //     setCurrentPage(page);
  //     const searchQuery = params.get("search") || "";
  //     const queryParams = searchQuery
  //       ? { search: searchQuery }
  //       : { page: page, limit: 10 };
  //     dispatch(startstopLoading(true));

  //     dispatch(getClients(queryParams)).then((res) => {
  //       dispatch(startstopLoading(false));
  //     });
  //   }
  // }, [location.search, dispatch, location.pathname]);

  useEffect(() => {
    if (location.pathname === "/clients") {
      const params = new URLSearchParams(location.search);
      const page = params.get("page") || 0;
      const searchQuery = params.get("search") || "";
      const sort = params.get("sort");
      const direction = params.get("direction");
      const startDate = params.get("startDate") || null;
      const endDate = params.get("endDate") || null;

      setCurrentPage(page);

      // restore sort from URL
      if (sort && direction) {
        setSortConfig({ key: sort, direction });
      }

      // Build queryParams with search and pagination
      const queryParams = {
        page,
        limit: 10,
        ...(searchQuery && { search: searchQuery }),
        ...(startDate && endDate && { startDate, endDate }),
        ...(sort && direction && { sort, direction })
      };

      dispatch(startstopLoading(true));
      dispatch(getClients(queryParams)).then(() => {
        dispatch(startstopLoading(false));
      });
    }
  }, [location.search, dispatch, location.pathname]);

  const updateURLParams = (
    newPage,
    createRange,
  ) => {
    const params = new URLSearchParams({
      page: newPage,
    });

    if (createRange?.startDate && createRange?.endDate) {
      params.set("startDate", createRange.startDate);
      params.set("endDate", createRange.endDate);
    }

    navigate(`/clients?${params.toString()}`, { replace: true });
  };

  const handleCreateDateRangeChange = (range) => {
    updateURLParams(currentPage, range);
  };

  return (
    <Layout>
      <Container fluid>
        <div className="comn-table-title">
          <h3>
            Clients <span>({getClientsList?.total ?? "0"})</span>
          </h3>
          <div className="d-flex gap-2">
            {/* {selectedUsers?.length > 0 ? <button onClick={handleExportButtonClick}>Export CSV</button> : null} */}
            <DatePickerComponent
              onDateRangeChange={handleCreateDateRangeChange}
              type={"eventDate"}

            />
            <button onClick={handleShow}>Create Client</button>

          </div>
        </div>
        <div className="filters">
          <div className="inner-filter-field">
            <div class="table-responsive new-table-size">
              <Table size="sm" className="table-cmn">
                <thead>
                  <tr>
                    <th>
                      <div className="user-checkbox">Name</div>
                    </th>
                    <th>Email</th>
                    <th
                      onClick={() => handleSort("createdAt")}
                      style={{ cursor: "pointer" }}
                    >
                      {" "}
                      Created Date{" "}
                      {sortConfig.key === "createdAt"
                        ? sortConfig.direction === "asc"
                          ? "↑"
                          : "↓"
                        : "↕"}
                    </th>
                    <th>Booked </th>
                    <th
                      onClick={() => handleSort("eventDate")}
                      style={{ cursor: "pointer" }}
                    >
                      Date of Event{" "}
                      {sortConfig.key === "eventDate"
                        ? sortConfig.direction === "asc"
                          ? "↑"
                          : "↓"
                        : "↕"}
                    </th>
                    <th>High Level Notes</th>
                    {/* <th></th> */}
                  </tr>
                </thead>
                <tbody>
                  {/* {getClientsList?.client?.length > 0 ? (
                    getClientsList?.client?.map((item, i) => ( */}
                  {clients?.length > 0 ? (
                    clients.map((item, i) => (
                      <tr key={i}>
                        <td>
                          <div className="first-user">
                            {/* <div className="remember-check">
                              <input
                                type="checkbox"
                                className="red"
                                id={`checkbox-${i}`}
                                onChange={(e) =>
                                  handleCheckboxChange(
                                    item._id,
                                    e.target.checked
                                  )
                                }
                              />
                            </div> */}
                            <div
                              style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}
                              onClick={() => handleClientDetail(item)}
                            >
                              <div style={{
                                width: "34px",
                                height: "34px",
                                borderRadius: "50%",
                                background: getAvatarColor(item?.fullName),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#132027",
                                flexShrink: 0,
                                textTransform: "uppercase",
                              }}>
                                {(item?.fullName || "?")[0]}
                              </div>
                              <div className="user-profile">
                                {capitalize(item?.fullName)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>{item?.email}</td>
                        <td>{formattedDate(item?.createdAt)}</td>
                        <td>{item.eventDate?.length ? "Yes" : "No"}</td>
                        {/* <td>{dateOfBookingEventWise(item?.eventDate)}</td> */}
                        <td>{item?.eventDate?.length ? formattedDate(item?.eventDate[0]) : '-'}</td>
                        <td>
                          <div
                            className="td-txt-bg"
                            onClick={() =>
                              handleViewNotes(
                                item?.notes || "No notes available"
                              )
                            }
                            style={{ cursor: "pointer" }}
                          >
                            View
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={6}
                        style={{ textAlign: "center", padding: "20px" }}
                      >
                        <div className="song-requests-data">
                          <div className="no-data-found">
                            <p>No Data</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
              {getClientsList?.total > 1 && (
                <PaginationComponent
                  currentPage={parseInt(
                    new URLSearchParams(location.search).get("page") || "0"
                  )}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  totalEntries={totalEntries}
                  entriesPerPage={entriesPerPage}
                />
              )}
            </div>
          </div>
        </div>
        <div className="mt-0">
          <p className="pb-4"></p>
        </div>
      </Container>

      {/* create client modal */}
      <CreateClientModal
        show={showModal}
        handleClose={handleClose}
        handleCreateClient={handleCreateClient}
        selectedClient={selectedClient}
      />
      {/* view notes modal  */}
      <NotesViewModal
        show={showModalNotes}
        handleClose={() => setShowModalNotes(false)}
        notes={selectedNotes}
      />
    </Layout>
  );
}
